import { useEffect, useState } from "react"
import { useLocation } from "@tanstack/react-router"

import { useBreakpoints } from "@productlane/ui"

import { AiWidgetLauncher } from "@/components/ai-widget-launcher"
import { ChangelogNotificationGate } from "@/components/changelog-notification-gate"
import { api } from "@/lib/api"
import { useMainNavState } from "@/lib/states/main-nav-state"
import { isWidgetDisabledRoute } from "@/lib/widget-routes"
import { useMainSidebarState } from "@/screens/app/sidebar"
import { useSupportWidgetStore } from "@/stores/support-widget-store"

// this component manages the lifecycle and state synchronization of the Productlane widget
export function ProductlaneWidgetController() {
	const location = useLocation()
	const { isMobile } = useBreakpoints()
	const isMainSidebarOpen = useMainSidebarState((s) => s.isOpen)
	const isForcedOpen = useSupportWidgetStore((s) => s.isForcedOpen)
	const releaseForcedWidget = useSupportWidgetStore((s) => s.release)
	const isLauncherHiddenByNav = useMainNavState((s) => s.launcherHidden)
	const [isWidgetOpen, setIsWidgetOpen] = useState(false)
	const userDataQuery = api.user.info.useQuery(undefined, {
		staleTime: Infinity,
		cacheTime: Infinity,
		refetchOnMount: true,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		useErrorBoundary: false,
	})

	const [widgetLoaded, setWidgetLoaded] = useState(false)

	useEffect(() => {
		const onLoaded = () => {
			setWidgetLoaded(true)
		}

		// @ts-expect-error Globald widget calls
		// eslint-disable-next-line
		window.Productlane.on("loaded", onLoaded)

		return () => {
			// @ts-expect-error Globald widget calls
			// eslint-disable-next-line
			window.Productlane.off("loaded", onLoaded)
		}
	}, [])

	// The pre-boot stub keeps one queued call per method, so a second `on` here
	// would replace the "loaded" listener and the widget would never report in.
	// After boot it still holds one callback per event, so "closed" does both jobs.
	useEffect(() => {
		if (!widgetLoaded) return

		const onOpened = () => setIsWidgetOpen(true)
		const onClosed = () => {
			setIsWidgetOpen(false)
			releaseForcedWidget()
		}
		// @ts-expect-error Globald widget calls
		// eslint-disable-next-line
		window.Productlane.on("opened", onOpened)
		// @ts-expect-error Globald widget calls
		// eslint-disable-next-line
		window.Productlane.on("closed", onClosed)

		return () => {
			// @ts-expect-error Globald widget calls
			// eslint-disable-next-line
			window.Productlane.off("opened", onOpened)
			// @ts-expect-error Globald widget calls
			// eslint-disable-next-line
			window.Productlane.off("closed", onClosed)
		}
	}, [releaseForcedWidget, widgetLoaded])

	const isRouteDisabled = isWidgetDisabledRoute(location.pathname)
	const hideForMobile = isMobile && !isMainSidebarOpen && !isForcedOpen
	// Disabling closes the widget, so an open chat outlives the nav sliding away.
	const hideForHiddenNav =
		isLauncherHiddenByNav && !isWidgetOpen && !isForcedOpen
	// An admin viewing as a customer must not reach our support as that customer.
	const isImpersonating = !!userDataQuery.data?.impersonatingFromUserId
	const shouldDisable =
		isRouteDisabled || hideForMobile || hideForHiddenNav || isImpersonating

	useEffect(() => {
		if (!widgetLoaded) return // wait until widget is loaded

		if (shouldDisable) {
			// @ts-expect-error Globald widget calls
			// eslint-disable-next-line
			window.Productlane.disable()
		} else {
			// @ts-expect-error Globald widget calls
			// eslint-disable-next-line
			window.Productlane.enable()
		}
	}, [widgetLoaded, shouldDisable])

	useEffect(() => {
		if (!widgetLoaded || isImpersonating) return
		if (!userDataQuery.data?.widgetUserToken) return
		// @ts-expect-error Globald widget calls
		// eslint-disable-next-line
		window.Productlane.set({
			userToken: userDataQuery.data.widgetUserToken,
		})
	}, [widgetLoaded, isImpersonating, userDataQuery.data?.widgetUserToken])

	// Set page context on route changes
	useEffect(() => {
		if (!widgetLoaded) return

		try {
			// @ts-expect-error Global widget calls
			// eslint-disable-next-line
			window.Productlane.setContext({ page: location.pathname })
		} catch (error) {
			console.warn("[Widget Controller] Failed to set context:", error)
		}
	}, [location.pathname, widgetLoaded])

	// The launcher owns the trial/free gating and waits for the widget to mount;
	// only skip it on routes where the widget is disabled (also keeps its
	// workspace queries off unauthenticated routes).
	if (shouldDisable) return null

	return (
		<>
			<ChangelogNotificationGate />
			<AiWidgetLauncher />
		</>
	)
}
